import { Component, Input, OnInit, Output, EventEmitter, Inject, Optional, OnDestroy, AfterViewInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { StudioConfigService } from 'src/app/services/studio-config.service';
import { PrintService } from 'src/app/services/print.service';
import moment from 'moment';
import { LoggerService } from '../../../services/logger.service';

interface AttendanceEvent {
  id: string;
  title: string;
  start: Date;
  end?: Date;
  appointmentType: string;
  studentName?: string;
  instructorName?: string;
  eventDetails?: any;
  resourceId?: string;
}

interface AttendanceGroup {
  instructor: string;
  instructorId: string;
  events: AttendanceEvent[];
}

@Component({
  selector: 'app-print-attendance',
  templateUrl: './print-attendance.component.html',
  styleUrls: ['./print-attendance.component.css']
})
export class PrintAttendanceComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() events: any[] = [];
  @Input() resources: any[] = [];
  @Input() currentDate: string = '';

  @Output() closeEvent = new EventEmitter<void>();

  studioLogo: string = '';
  studioName: string = '';
  studioAddress: string = '';
  agendaMasterLogo: string = 'assets/images/AMDCBlackHorizontalJPG.jpg';

  attendanceGroups: AttendanceGroup[] = [];
  formattedDate: string = '';

  componentId: string = '';

  constructor(
    private studioConfigService: StudioConfigService,
    private printService: PrintService,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any,
    @Optional() public dialogRef: MatDialogRef<PrintAttendanceComponent>
  ) {}

    ngOnInit(): void {
    // Generate unique ID for this instance
    this.componentId = 'print-attendance-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);

    // If we're in a dialog, use dialog data, otherwise use input properties
    if (this.dialogData) {
      // Access data from the nested data property
      const data = this.dialogData.data || this.dialogData;
      this.events = data.events || [];
      this.resources = data.resources || [];
      this.currentDate = data.currentDate || '';
    }

    // Convert resources to array if it's an object
    if (this.resources && !Array.isArray(this.resources)) {
      this.resources = Object.values(this.resources);
    }

    this.loadStudioInfo();
    this.processEventsForAttendance();
    this.formatCurrentDate();
  }

  ngAfterViewInit(): void {
    // Clean up stale containers after view is initialized
    setTimeout(() => {
      this.cleanupStaleContainers();
    }, 100);
  }

  private cleanupStaleContainers(): void {
    // Clean up any stale containers from previous instances
    const allContainers = document.querySelectorAll('.print-attendance-container');
    const thisContainer = document.querySelector(`[data-component-id="${this.componentId}"]`);

    if (allContainers.length > 1 && thisContainer) {
      allContainers.forEach(container => {
        // Remove containers that don't belong to this instance
        if (container !== thisContainer) {
          container.remove();
        }
      });
    }
  }

  private loadStudioInfo(): void {
    this.studioConfigSubscription = this.studioConfigService.getStudioConfig().subscribe(
      (config) => {
        if (config) {
          this.studioLogo = config?.['field_studio_logo']?.[0]?.url || '';
          this.studioName = config?.['field_studio_name']?.[0]?.value || '';

          // Format address
          const address = config?.['field_studio_address']?.[0];
          if (address) {
            const addressParts = [];
            if (address.address_line1) addressParts.push(address.address_line1);
            if (address.locality) addressParts.push(address.locality);
            if (address.administrative_area) addressParts.push(address.administrative_area);
            if (address.postal_code) addressParts.push(address.postal_code);
            this.studioAddress = addressParts.join(', ');
          }
        }
      },
      (error) => {
        LoggerService.error('Error loading studio config:', error);
        this.studioName = 'Music Studio';
      }
    );
  }

      private processEventsForAttendance(): void {
    if (!this.events || this.events.length === 0) {
      return;
    }

    const processedEventIds = new Set<string>(); // Track processed events to avoid duplicates
    const allEvents: AttendanceEvent[] = [];

    // Filter and process events that have students
    const validEvents = this.events.filter(event => {
      const hasType = event.appointmentType &&
        (event.appointmentType === 'lesson' ||
         event.appointmentType === 'group_lesson' ||
         event.appointmentType === 'services');
      const hasStudent = this.hasStudentInfo(event);

      return hasType && hasStudent;
    });

    validEvents.forEach(event => {
      // Skip if we've already processed this event ID
      if (processedEventIds.has(event.id)) {
        return;
      }
      processedEventIds.add(event.id);

      const instructorInfo = this.getInstructorInfo(event);

      // Ensure we have a proper Date object for start time
      let startDate: Date;
      if (event.start instanceof Date) {
        startDate = event.start;
      } else if (event.start) {
        startDate = new Date(event.start);
      } else {
        return; // Skip events without valid start time
      }

      // Ensure we have a proper Date object for end time
      let endDate: Date | undefined;
      if (event.end instanceof Date) {
        endDate = event.end;
      } else if (event.end) {
        endDate = new Date(event.end);
      }

      // Process the event for attendance
      const attendanceEvent: AttendanceEvent = {
        id: event.id,
        title: event.title || '',
        start: startDate,
        end: endDate,
        appointmentType: event.appointmentType,
        studentName: this.getStudentName(event),
        instructorName: instructorInfo.name,
        eventDetails: event.eventDetails || event._def?.extendedProps?.eventDetails,
        resourceId: event.resourceId
      };

      allEvents.push(attendanceEvent);
    });

    // Sort all events by time only (not by instructor)
    allEvents.sort((a, b) => a.start.getTime() - b.start.getTime());

    // Create a single group with all events sorted by time
    this.attendanceGroups = [{
      instructor: 'All Instructors',
      instructorId: 'all',
      events: allEvents
    }];

  }

  private hasStudentInfo(event: any): boolean {
    // Check if event has student information
        return !!(
      event.studentDetails?.studentName ||
      event.eventDetails?.data?.student_name ||
      event._def?.extendedProps?.studentDetails?.studentName ||
      event._def?.extendedProps?.eventDetails?.data?.student_name ||
      event.title?.includes('Student:') ||
      event.title?.includes('student') ||
      (event.appointmentType === 'group_lesson' && event.eventDetails?.data?.field_students) ||
      // Check for any event that's not just a schedule block
      (event.appointmentType && event.appointmentType !== 'schedules')
    );
  }

  private getStudentName(event: any): string {
    // Try different ways to get student name
    if (event.studentDetails?.studentName) {
      return event.studentDetails.studentName;
    }

    if (event.eventDetails?.data?.student_name) {
      return event.eventDetails.data.student_name;
    }

    if (event._def?.extendedProps?.studentDetails?.studentName) {
      return event._def.extendedProps.studentDetails.studentName;
    }

    if (event._def?.extendedProps?.eventDetails?.data?.student_name) {
      return event._def.extendedProps.eventDetails.data.student_name;
    }

    // For group lessons, get all student names
    if (event.appointmentType === 'group_lesson') {
      const students = event.eventDetails?.data?.field_students ||
                      event._def?.extendedProps?.eventDetails?.data?.field_students;
      if (students && Array.isArray(students)) {
        return students.map(s => s.title || s.name || 'Student').join(', ');
      }
    }

    // Try to extract from title
    if (event.title && event.title.includes('Student:')) {
      const match = event.title.match(/Student:\s*([^,\n]+)/);
      if (match) {
        return match[1].trim();
      }
    }

    return 'Student';
  }

  private getInstructorInfo(event: any): { id: string, name: string } {
    // Try to get instructor info from various sources
    let instructorId = '';
    let instructorName = '';

    // Check resource info first (most reliable)
    if (event.resourceId && this.resources && Array.isArray(this.resources)) {
      const resource = this.resources.find(r => r.id == event.resourceId); // Use == for loose comparison
      if (resource) {
        instructorId = resource.id;
        instructorName = resource.title || resource.name || resource.display_name || '';
      }
    }

    // Try event details
    if (!instructorName && event.eventDetails?.data) {
      const data = event.eventDetails.data;
      if (data.instructor_name) {
        instructorName = data.instructor_name;
        instructorId = data.instructor_id || instructorId;
      }
    }

    // Try extended props
    if (!instructorName && event._def?.extendedProps?.eventDetails?.data) {
      const data = event._def.extendedProps.eventDetails.data;
      if (data.instructor_name) {
        instructorName = data.instructor_name;
        instructorId = data.instructor_id || instructorId;
      }
    }

    // Try to extract from event title as last resort
    if (!instructorName && event.title) {
      const titleMatch = event.title.match(/Instructor:\s*([^,\n]+)/i);
      if (titleMatch) {
        instructorName = titleMatch[1].trim();
      }
    }

    // Default fallback with better naming
    return {
      id: instructorId || event.resourceId || 'unknown',
      name: instructorName || (event.resourceId ? `Instructor ${event.resourceId}` : 'Instructor')
    };
  }

  private formatCurrentDate(): void {
    if (this.currentDate) {
      this.formattedDate = moment(this.currentDate).format('dddd, MMMM Do, YYYY');
    } else {
      this.formattedDate = moment().format('dddd, MMMM Do, YYYY');
    }

  }

  formatTime(date: Date): string {
    return moment(date).format('h:mm A');
  }

  formatDuration(start: Date, end?: Date): string {
    if (!end) {
      return ''; // No duration if no end time
    }
    const duration = moment.duration(moment(end).diff(moment(start)));
    const minutes = duration.asMinutes();

    if (minutes < 60) {
      return `${minutes} min`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      if (remainingMinutes === 0) {
        return `${hours} hr`;
      } else {
        return `${hours} hr ${remainingMinutes} min`;
      }
    }
  }

  formatEventType(type: string): string {
    switch (type) {
      case 'lesson':
        return 'Lesson';
      case 'group_lesson':
        return 'Group Lesson';
      case 'services':
        return 'Service';
      case 'schedules':
        return 'Schedule';
      default:
        return type;
    }
  }

  closeView(): void {
    if (this.dialogRef) {
      // If we're in a dialog, close the dialog
      this.dialogRef.close();
    } else {
      // Otherwise emit the close event
      this.closeEvent.emit();
    }
  }

  // Studio config is app-lifetime; released so each printed sheet can be collected.
  private studioConfigSubscription: Subscription;

  ngOnDestroy(): void {
    this.studioConfigSubscription?.unsubscribe();
    // Clean up this component's container when destroyed
    const thisContainer = document.querySelector(`[data-component-id="${this.componentId}"]`);
    if (thisContainer) {
      thisContainer.remove();
    }
  }

  print(): void {
    // Build the HTML content for printing
    let content = `
      <div class="attendance-container">
        <div class="attendance-header">
          ${this.studioLogo ? `<img src="${this.studioLogo}" alt="${this.studioName}" class="studio-logo"><br>` : ''}
          <div class="attendance-title">Daily Attendance Sheet</div>
          <div class="attendance-date">${this.formattedDate}</div>
        </div>
    `;

    if (this.attendanceGroups.length === 0) {
      content += `
        <div class="no-events">
          <p>No lessons or appointments scheduled for this date.</p>
        </div>
      `;
    } else {
      content += `
        <table class="attendance-table">
          <thead>
            <tr>
              <th class="time-col">Time</th>
              <th class="student-col">Student Name</th>
              <th class="instructor-col">Instructor</th>
              <th class="type-col">Type</th>
              <th class="duration-col">Duration</th>
              <th class="signature-col">Student Signature</th>
            </tr>
          </thead>
          <tbody>
      `;

      // Add all events to the table
      this.attendanceGroups.forEach(group => {
        group.events.forEach(event => {
          content += `
            <tr>
              <td class="time-cell">${this.formatTime(event.start)}</td>
              <td class="student-cell">${event.studentName || 'Student'}</td>
              <td class="instructor-cell">${event.instructorName}</td>
              <td class="type-cell">
                <span class="type-${event.appointmentType}">
                  ${this.formatEventType(event.appointmentType)}
                </span>
              </td>
              <td class="duration-cell">${this.formatDuration(event.start, event.end)}</td>
              <td class="signature-cell">
                <div class="signature-line"></div>
              </td>
            </tr>
          `;
        });
      });

      content += `
          </tbody>
        </table>
      `;
    }

    // Add footer
    content += `
      <div class="attendance-footer">
        <div class="footer-notes">
          <p><strong>Notes:</strong></p>
          <div class="notes-area">
            <div class="note-line"></div>
            <div class="note-line"></div>
            <div class="note-line"></div>
          </div>
        </div>
      </div>
    </div>
    `;

    // Define custom styles for the attendance sheet
    const styles = `
      @page {
        size: letter portrait;
        margin: 0.4in;
      }

      body {
        font-family: Arial, sans-serif;
        font-size: 12px;
        line-height: 1.3;
        color: #333;
      }

      .attendance-container {
        width: 100%;
        max-width: 8.5in;
        margin: 0 auto;
      }

      .attendance-header {
        text-align: center;
        border-bottom: 2px solid #333;
        padding-bottom: 10px;
        margin-bottom: 15px;
        page-break-after: avoid;
        page-break-inside: avoid;
      }

      .studio-logo {
        max-height: 50px;
        max-width: 120px;
        object-fit: contain;
        margin-bottom: 5px;
      }

      .attendance-title {
        font-size: 18px;
        font-weight: bold;
        margin: 5px 0 3px 0;
      }

      .attendance-date {
        font-size: 14px;
        color: #555;
        margin: 3px 0;
      }

      .no-events {
        text-align: center;
        padding: 40px;
        color: #666;
        font-style: italic;
      }

      .attendance-table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 20px;
        font-size: 11px;
        page-break-before: auto;
        page-break-after: auto;
        page-break-inside: auto;
      }

      .attendance-table thead {
        display: table-header-group;
      }

      .attendance-table tbody {
        display: table-row-group;
      }

      .attendance-table th {
        background-color: #f8f9fa;
        border: 1px solid #000;
        padding: 6px 5px;
        text-align: left;
        font-weight: bold;
        font-size: 11px;
      }

      .attendance-table td {
        border: 1px solid #000;
        padding: 8px 5px;
        vertical-align: middle;
        font-size: 10px;
      }

      .attendance-table tr {
        page-break-inside: avoid;
        page-break-after: auto;
      }

      /* Keep header with at least 2 rows of data */
      .attendance-table thead tr {
        page-break-after: avoid;
      }

      /* First few rows should stay with header if possible */
      .attendance-table tbody tr:nth-child(1),
      .attendance-table tbody tr:nth-child(2) {
        page-break-before: avoid;
      }

      /* Column widths */
      .time-col { width: 12%; }
      .student-col { width: 25%; }
      .instructor-col { width: 20%; }
      .type-col { width: 13%; text-align: center; }
      .duration-col { width: 10%; }
      .signature-col { width: 20%; }

      /* Cell styling */
      .time-cell { font-weight: 500; }
      .student-cell { font-weight: 500; }
      .instructor-cell { color: #666; }
      .type-cell { text-align: center; }

      /* Event type badges */
      .type-lesson {
        background-color: #e3f2fd;
        color: #1976d2;
        padding: 2px 6px;
        border-radius: 3px;
        font-size: 10px;
        font-weight: 500;
      }

      .type-group_lesson {
        background-color: #f3e5f5;
        color: #7b1fa2;
        padding: 2px 6px;
        border-radius: 3px;
        font-size: 10px;
        font-weight: 500;
      }

      .type-services {
        background-color: #e8f5e9;
        color: #388e3c;
        padding: 2px 6px;
        border-radius: 3px;
        font-size: 10px;
        font-weight: 500;
      }

      /* Signature line */
      .signature-line {
        border-bottom: 1px solid #333;
        width: 100%;
        margin-top: 15px;
      }

      /* Footer */
      .attendance-footer {
        margin-top: 20px;
        padding-top: 15px;
        border-top: 1px solid #ddd;
        page-break-inside: avoid;
      }

      .footer-notes {
        margin-bottom: 15px;
      }

      .note-line {
        border-bottom: 1px solid #ccc;
        margin: 10px 0;
      }

      @media print {
        .attendance-table thead {
          display: table-header-group;
        }

        .attendance-table tr {
          page-break-inside: avoid;
        }
      }
    `;

    // Use the PrintService to generate and open the print window
    this.printService.print(content, {
      title: 'Daily Attendance Sheet',
      styles: styles,
      promptPrint: true,  // This will trigger window.print() automatically
      showSchoolName: true
    });

    // Close the dialog after a short delay
    setTimeout(() => {
      if (this.dialogRef) {
        this.dialogRef.close();
      }
    }, 1000);  // Give a bit more time for the print window to open
  }
}
